import { useState, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import * as reportAccessService from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.helpers';

import type { ReportUser } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';

export type ReportUsersMap = Record<string | number, ReportUser[] | undefined>;

interface UseReportUsersResult {
  reportUsers: ReportUsersMap;
  setReportUsers: Dispatch<SetStateAction<ReportUsersMap>>;
  fetchReportUsers: (reportId: string | number) => Promise<ReportUser[] | null>;
}

export const useReportUsers = (
  users: ReportUser[],
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
  userId: string | undefined,
  clientId: string | number | undefined
): UseReportUsersResult => {
  const [reportUsers, setReportUsers] = useState<ReportUsersMap>({});

  const fetchReportUsers = useCallback(
    async (reportId: string | number): Promise<ReportUser[] | null> => {
      if (!userId || !reportId || !clientId || loading) return null;

      setLoading(true);
      try {
        const assignedUsers = (await reportAccessService.fetchReportUsers({
          userId,
          reportId,
          clientId,
        })) as ReportUser[] | undefined;

        if (Array.isArray(assignedUsers)) {
          const updatedUsers = users.map((user) => ({
            ...user,
            hasAccess: assignedUsers.some(
              (reportUser) => Number(reportUser.id) === Number(user.id)
            ),
          }));

          setReportUsers((prev) => ({
            ...prev,
            [reportId]: updatedUsers,
          }));

          return updatedUsers;
        }
      } catch (error) {
        console.error('Error fetching report users:', error);
      } finally {
        setLoading(false);
      }

      return null;
    },
    [userId, clientId, users, loading, setLoading]
  );

  return {
    reportUsers,
    setReportUsers,
    fetchReportUsers,
  };
};

export default useReportUsers;
